import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "../../lib/api";
import { startSchoolOnboarding } from "../../lib/schools";
import { Button } from "../ui";
import {
  dismissStudioSetupBar,
  getStudioSetupProgress,
  isStudioSetupBarDismissed,
  shouldEnsurePracticeSetup,
  undismissStudioSetupBar,
} from "../../lib/studioSetupProgress";

/**
 * Thin dismissible progress strip under Studio sub-tabs.
 * Always opens the named next gap; may re-enable Practice setup when empty.
 */
export default function StudioSetupStatusBar({
  school,
  venues = [],
  classes = [],
  products = [],
  onSchoolUpdated,
  onSwitchTab,
}) {
  const schoolId = school?.id;
  const [dismissed, setDismissed] = useState(() => isStudioSetupBarDismissed(schoolId));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setDismissed(isStudioSetupBarDismissed(schoolId));
  }, [schoolId]);

  const progress = getStudioSetupProgress({ venues, classes, products });
  const next = progress.next;

  if (!school || dismissed || progress.done || !next) return null;

  function handleDismiss() {
    dismissStudioSetupBar(school.id);
    setDismissed(true);
    toast.message("Setup progress hidden", {
      action: {
        label: "Undo",
        onClick: () => {
          undismissStudioSetupBar(school.id);
          setDismissed(false);
        },
      },
    });
  }

  async function goToNextGap() {
    setBusy(true);
    try {
      if (shouldEnsurePracticeSetup({ school, venues, classes, products })) {
        const doc = await startSchoolOnboarding(school.id);
        onSchoolUpdated?.(doc);
      }
      onSwitchTab?.(next.tab);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="flex flex-wrap items-center gap-x-2 gap-y-1.5 rounded-lg border border-black/[0.08] bg-surface px-3 py-2"
      data-testid="school-studio-setup-bar"
    >
      <p
        className="min-w-0 flex-1 basis-[12rem] font-ui text-[13px] font-medium text-ink text-pretty"
        role="status"
      >
        Studio setup · {progress.completed} of {progress.total}
        <span className="text-ink/70">{` — Next: ${next.nextPhrase}`}</span>
      </p>
      <div className="flex items-center gap-1 shrink-0 ml-auto">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={busy}
          onClick={goToNextGap}
          data-testid="school-studio-setup-finish"
          aria-label={next.label}
          className="min-h-[44px]"
        >
          {busy ? "Opening…" : next.label}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleDismiss}
          aria-label="Dismiss studio setup progress"
          data-testid="school-studio-setup-dismiss"
          className="!min-h-[44px] !min-w-[44px] !px-0"
        >
          <X size={16} strokeWidth={2.5} aria-hidden />
        </Button>
      </div>
    </div>
  );
}
